import { Controller, RegisterOptions } from 'react-hook-form';
import type { Questionnaire, FieldType } from '@/typings/Questionnaire';
import {
  AddressIntl,
  AddressUS,
  Checkboxes,
  Currency,
  DatePicker,
  InputOnBlur,
  TextAreaOnBlur,
  PhoneInput,
  Percent,
  RadioButtons,
  Select,
  NameOnBlur,
  NumberInput,
  TextArea,
  Name,
  Input,
} from '@/components/DesignLibrary/Atoms';
import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import getFieldValue from '@helpers/questionnaires/getFieldValue';
import evaluateExpression from '@helpers/evaluateExpression';
import { useFormContext } from '@/context/FormContext';
import { getValidationRules } from '@helpers/validation/schema';
import Label from '@/components/DesignLibrary/Atoms/shared/Label';
import Linked from '@/components/DesignLibrary/Atoms/shared/Linked';
import removeNullish from '@helpers/removeNullish';
import getAllLinkedFieldKeys from '@helpers/getAllLinkedFieldKeys';
import { useOpenDataReferencePane } from '@panes/dataReferenceHooks';
import AILabel from '@/components/AILabel';
import { AnimatePresence, m } from 'framer-motion';
import { usePrevious } from '@hooks';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import trackEvents from '@/helpers/trackEvents';

import Spacer from '@components/Spacer';
import getAiAgentResponse from '@/tsClient/questionnaires/getAiAgentResponse';
import Modal from '@/components/Modal';
import Shimmer from '@/components/Shimmer';
import BannerStatus from '@/components/DesignLibrary/BannerStatus';
import ReferencedList from '@/components/DesignLibrary/Atoms/shared/ReferencedList';
import { Table } from '@/components/DesignLibrary/Table';
import TBody from '@/components/DesignLibrary/Table/TBody';
import THead from '@/components/DesignLibrary/Table/THead';
import TableRow from '@/components/DesignLibrary/Table/TableRow';
import TableCell from '@/components/DesignLibrary/Table/TableCell';

import { useFeatureFlag } from '@/providers/FeatureFlags';
import getLinkedFieldKeyDetails from '@/tsClient/questionnaires/getLinkedFieldKeyDetails';
import EditQuestionnaireFieldModal from '../EditQuestionnaireFieldModal';
import List from '../List';
import FormFieldTable from '../Table';
import MedianIncomeWidget from '../MedianIncomeWidget';
import MonthlyIncomeRatio from '../MonthlyIncomeRatio';

import s from './FormField.module.scss';
import renderPopulatedFrom from './renderPopulatedFrom';
import AiAgentData from '../ConnectedDataPanel/AutofillData';
import isEmptyObject from './utils/isEmptyObject';
import CreditorSelect from '../CreditorSelect';

type LinkedFieldDetailsResponse = NonNullable<Awaited<ReturnType<typeof getLinkedFieldKeyDetails>>>;

type FormFieldProps = {
  field: Questionnaire.FieldCommon;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules: any;
  isEnabled: boolean;
  isEditor?: boolean;
  onClickPopulatedFrom?: (e: MouseEvent) => void;
  validationSchema?: Record<string, RegisterOptions>;
  fieldsAutofilling?: string[];
};

const typeNeedsRef = (type: FieldType): boolean => !['list', 'single-select', 'explanation'].includes(type);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentsMap: { [key in FieldType]: React.FC<any> } = {
  'address-international': AddressIntl,
  'address-us': AddressUS,
  break: () => null,
  'creditor-select': CreditorSelect,
  currency: Currency,
  date: DatePicker,
  explanation: Label,
  list: List,
  'long-text': TextArea,
  'median-income': MedianIncomeWidget,
  'monthly-income': MonthlyIncomeRatio,
  'multi-select': Checkboxes,
  name: Name,
  number: NumberInput,
  numeric: Input,
  percent: Percent,
  'phone-number': PhoneInput,
  selection: Select,
  'short-text': Input,
  'single-select': RadioButtons,
  'ssn': Input,
  table: FormFieldTable,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentsOnBlurMap: { [key in FieldType]: React.FC<any> } = {
  'address-international': AddressIntl,
  'address-us': AddressUS,
  break: () => null,
  'creditor-select': CreditorSelect,
  currency: Currency,
  date: DatePicker,
  explanation: Label,
  list: List,
  'long-text': TextAreaOnBlur,
  'median-income': MedianIncomeWidget,
  'monthly-income': MonthlyIncomeRatio,
  'multi-select': Checkboxes,
  name: NameOnBlur,
  number: NumberInput,
  numeric: InputOnBlur,
  percent: Percent,
  'phone-number': PhoneInput,
  selection: Select,
  'short-text': InputOnBlur,
  'single-select': RadioButtons,
  'ssn': InputOnBlur,
  table: FormFieldTable,
};

const FormField: React.FC<FormFieldProps> = ({
  field,
  control,
  rules,
  isEnabled,
  isEditor,
  onClickPopulatedFrom,
  validationSchema,
  fieldsAutofilling,
}) => {
  trackEvents.track('Render - FormField', field.key);
  const [isVisible, setIsVisible] = useState(!isEnabled);
  const [isEvaluated, setIsEvaluated] = useState(false);
  const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false);
  const [isAutofilling, setIsAutofilling] = useState(false);
  const [showAutofillEditBanner, setShowAutofillEditBanner] = useState(false);
  const [linkedDetailsModalData, setLinkedDetailsModalData] = useState<LinkedFieldDetailsResponse | null>(null);
  const betaPerformanceImprovements = useFeatureFlag('beta-performance-improvements');

  const {
    aiAutofillActions,
    defaultValues,
    formMethods,
    fieldsByKey,
    fieldsById,
    setValue,
    getFieldSource,
    questionnaireId,
    userWorkflowId,
  } = useFormContext();

  const { getData: deleteQuestionnaireResponse } = useNoodleApi(tsClient.questionnaires.deleteQuestionnaireResponse);

  const { getData: fetchLinkedFieldDetails, fetchingState: { isFetching: isFetchingLinkedDetails } } = useNoodleApi(getLinkedFieldKeyDetails);

  const prevIsVisible = usePrevious(isVisible);

  const setDataReferenceId = useOpenDataReferencePane();

  const fieldSource = getFieldSource(field.key);

  const conditionVariables = formMethods.watch(field.conditionExpression?.variables?.map(v => v.referenceId) || []);

  const [referencedList, setReferencedList] = useState<{
    title: string;
    values: Array<{
        property: string;
        value: string;
    }> | null;
    onEditClick: () => void;
    onClose: () => void;
    type?: "autofill" | "ai-autofill" | "questionnaire";
      } | null>(null);
  const [fieldKey, _setFieldKey] = useState(field.key);

  const {
    data: aiAgentResponse,
    getData: getAiAgentResponseFn,
    fetchingState: {
      isFetching: isFetchingAiAgentResponse,
    },
  } = useNoodleApi(getAiAgentResponse);

  useEffect(() => {

    if (aiAgentResponse && aiAutofillActions?.includes(field.autofill?.action || '')) {
      setReferencedList({
        onClose: () => {
          setReferencedList(null);
        },
        onEditClick: () => {},
        title: '',
        type: 'ai-autofill',
        values: [
          {
            property: 'externalData',
            value: JSON.stringify(aiAgentResponse.externalData || {}),
          },
          {
            property: 'fieldInputs',
            value: JSON.stringify(aiAgentResponse.fieldInputs || {}),
          },
          {
            property: 'internalData',
            value: JSON.stringify(aiAgentResponse.internalData || []),
          },
          {
            property: 'fieldOutputs',
            value: JSON.stringify(aiAgentResponse.fieldOutputs || {}),
          },
          {
            property: 'fieldKey',
            value: field.key,
          },
          {
            property: 'expression',
            value: field.conditionExpression?.expression || field.autofill?.expression?.expression || '',
          },
          {
            property: 'description',
            value: field.autofill?.description || '',
          },
          {
            property: 'variables',
            value: JSON.stringify(field.conditionExpression?.variables || field.autofill?.expression?.variables || []),
          },
          {
            property:'aboutAiAgent',
            value: JSON.stringify(aiAgentResponse || {}),
          },
        ],
      });
    }
    else {
      setReferencedList(null);
    }
  }, [aiAgentResponse]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (fieldsAutofilling?.includes(field.key)) {
      setIsAutofilling(true);
    } else {
      timeout = setTimeout(() => {
        setIsAutofilling(false);
      }, 300);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [fieldsAutofilling, field.key]);

  useEffect(() => {
    if (prevIsVisible && !isVisible) {
      setValue({ key: field.key, value: null });
      deleteQuestionnaireResponse({
        fieldTemplateKey: field.key,
        questionnaireId,
        rowResponseId: null,
      });
    }
  }, [isVisible, prevIsVisible, field.key, formMethods, questionnaireId, deleteQuestionnaireResponse, setValue]);

  useEffect(() => {
    const { conditionExpression } = field;

    if (conditionExpression && isEnabled) {
      const variablesWithValues = conditionExpression.variables?.map((variable, idx) => ({
        ...variable,
        value: getFieldValue({
          field: fieldsByKey[variable.referenceId],
          value: conditionVariables[idx],
        }),
      })) || [];
      const result = evaluateExpression({
        expression: conditionExpression.expression,
        id: 'FormField-condition',
        variables: variablesWithValues.reduce((acc, val) => ({
          ...acc,
          [val.variableName]: val.value,
        }), {}),
      });
      setIsVisible(Boolean(result));
    } else {
      setIsVisible(true);
    }
    setIsEvaluated(true);
  }, [field, conditionVariables, isEnabled, fieldsByKey]);

  useEffect(() => {
    if (!isVisible && isEvaluated) {
      formMethods.unregister(field.key);
    }
  }, [isVisible, isEvaluated, field.key, formMethods]);

  const defaultValueOption = useMemo(() => field.options && Array.isArray(field.options)
    ? field.options.find(option => option.isDefault === true)
    : undefined, [field.options]);

  useEffect(() => {
    if (
      (field.type === 'selection' || field.type === 'single-select')
      && defaultValueOption
      && !formMethods.getValues(field.key)
    ) {
      setValue({ key: field.key, value: defaultValueOption.key });
    }
  }, [field.type, field.key, defaultValueOption, formMethods, isVisible, setValue]);

  useEffect(() => {
    if (!formMethods.watch(field.key) && !fieldSource && field.linkedFields?.length > 0) {
      const fieldTemplateKeys = getAllLinkedFieldKeys({
        field,
        fieldsById,
      });
      const linkedFieldValues = formMethods.watch(fieldTemplateKeys).filter(removeNullish);
      if (linkedFieldValues[0]) {
        setValue({ key: field.key, source: 'linked-field', value: linkedFieldValues[0] });
      }
    }
  }, [field, setValue, fieldsById, formMethods, fieldSource]);

  const Component = betaPerformanceImprovements ? componentsOnBlurMap[field.type] : componentsMap[field.type];

  if (!Component || !isVisible) return null;

  formMethods.register(field.key, getValidationRules(field));

  const { key, ...restProps } = field;

  const isLinkedField = (field.numLinkedFields || 0) > 0;

  const callAiAgentResponseFn = (): void => {
    if (questionnaireId) {
      getAiAgentResponseFn({
        fieldKey,
        questionnaireId,
      });
    }
  };

  const handleLinkedClick = async (): Promise<void> => {
    const response = await fetchLinkedFieldDetails({
      linkedFieldKeys: field.linkedFieldKeys || [],
      questionnaireTemplateId: field.questionnaireTemplateId || '',
    });
    if (response && response.data && !response.error) {
      setLinkedDetailsModalData(response.data);
    }
  };

  return (
    <>
      {isEditFieldModalOpen && (
        <EditQuestionnaireFieldModal
          field={{
            ...field,
            linkedFields: fieldsByKey[field.key]?.linkedFields,
          }}
          control={control}
          rules={rules}
          onClose={() => setIsEditFieldModalOpen(false)}
          onSubmit={() => setIsEditFieldModalOpen(false)}
        />
      )}
      <Controller
        name={field.key}
        control={control}
        shouldUnregister={false}
        defaultValue={
          (field.type === 'selection' || field.type === 'single-select')
            ? defaultValueOption?.key
            : defaultValues[field.key]
        }
        rules={rules}
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error },
        }) => (
          <div className={s.fieldWrapper}>
            <AnimatePresence>
              {showAutofillEditBanner && <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={s.autofillEditBanner}>
                <BannerStatus compact label="You are editing an autofilled field." status='warning' />
              </m.div>}
            </AnimatePresence>
            <Component
              key={key}
              {...restProps}
              description={field.description || field.descriptionText}
              isPreview={!isEnabled}
              isEditor={isEditor}
              triggerOnChangeOnBlur={!!betaPerformanceImprovements}
              field={field}
              disabled={field.disabled || isEditFieldModalOpen || isAutofilling}
              required={field.isRequired}
              optional={!field.isRequired}
              value={value}
              {...field.type === 'median-income' && { cmiAutofillDescription: field.autofill?.description }}
              onChange={(newValue: typeof value) => {
                onChange(newValue);
              }}
              onBlur={async () => {
                onBlur();
                setShowAutofillEditBanner(false);
                if (error?.message) {
                  await formMethods.trigger();
                }
              }}
              onFocus={() => {
                if ((isLinkedField || field.autofill) && (value && !isEmptyObject(value)) && isEnabled) {
                  if (isLinkedField) {
                    if (document.activeElement instanceof HTMLElement) {
                      document.activeElement.blur();
                    }
                    setIsEditFieldModalOpen(true);
                  } else {
                    setShowAutofillEditBanner(true);
                  }
                }
              }}
              ref={typeNeedsRef(field.type) ? ref : null}
              control={control}
              rules={rules}
              error={error?.message}
              fieldsAutofilling={fieldsAutofilling}
            />
            <div className={s.linkedAndPopulated}
              {...isEditor
                  && (field.type === 'list' || (field.type === 'explanation' && (field?.listItemTemplates ?? []).length > 0) || field.type === 'table')
                  && {
                    style: {
                      marginBottom: 24,
                    },
                  }}
            >
              <AnimatePresence>
                {isAutofilling ? <AILabel label="Agent actively working..." /> : ''}
              </AnimatePresence>
              {renderPopulatedFrom({
                canReset: isEnabled && !isEditor,
                field,
                onClick: (e) => {
                  if(userWorkflowId) {
                    if (aiAutofillActions?.includes(field.autofill?.action || '')) {
                      callAiAgentResponseFn();
                    }
                    else {
                      setReferencedList({
                        onClose: () => {
                          setReferencedList(null);
                        },
                        onEditClick: () => {},
                        title: '',
                        type: 'autofill',
                        values: [
                          {
                            property: 'expression',
                            value: field.autofill?.expression?.expression || field.conditionExpression?.expression || '',
                          },
                          {
                            property: 'description',
                            value: field.autofill?.description || '',
                          },
                          {
                            property: 'variables',
                            value: JSON.stringify(field.autofill?.expression?.variables || field.conditionExpression?.expression || []),
                          },
                        ],
                      });
                    }
                  }
                  onClickPopulatedFrom?.(e);
                },
                prominent: !isEnabled && !isEditor,
              })}
              {field.numLinkedFields !== undefined && field.numLinkedFields > 0 && (
                <Linked
                  number={field.numLinkedFields}
                  onClick={handleLinkedClick}
                />
              )}
              {field.referencedList?.isReferencingExistingList && (
                <ReferencedList
                  referencedList={field.referencedList}
                />
              )}
              <AnimatePresence>
                {isAutofilling ? <AILabel label="Agent actively working..." /> : ''}
              </AnimatePresence>
            </div>
          </div>
        )}
      />
      {!isEditor && field.type === 'explanation' && field.listItemTemplates?.map((subField) => (
        <React.Fragment key={subField.id}>
          <Spacer size={16} />
          <FormField
            field={{
              ...subField,
              ...subField.type === 'single-select' && { native: true, placeholder: 'Select an option', type: 'selection' },
              ...subField.type === 'currency' && { nativeSelect: true },
              compact: true,
              disabled: field.disabled,
              listItemTemplates: Object.values(fieldsById).filter((f) => f.listFieldTemplateId === subField.id).map(
                f => ({
                  ...f,
                  disabled: field.disabled,
                }),
              ),
            }}
            isEditor={false}
            isEnabled={isEnabled}
            control={control}
            rules={validationSchema?.[subField.key]}
            validationSchema={validationSchema}
            fieldsAutofilling={fieldsAutofilling}
            onClickPopulatedFrom={(e: React.MouseEvent) => {
              e.stopPropagation();
              if (
                subField.autofill?.expression
                && subField.autofill.expression.variables[0]?.referenceType === 'data-reference-slug'
              ) {
                setDataReferenceId({ dataReferenceId: subField.autofill.expression.variables[0].referenceId });
              }
            }}
          />
          {isAutofilling ? <AILabel label="Agent actively working..." /> : ''}
        </React.Fragment>
      ))}
      {
        isFetchingAiAgentResponse && (
          <Modal title={`${field.label} Data`}>
            <Shimmer />
          </Modal>
        )
      }
      {
        referencedList && (
          <Modal onClose={() => setReferencedList(null)} title={`${field.label} Data`}>
            <div className={s.modalContent}>
              <AiAgentData data={referencedList} hideCloseIcon={true} field={field} />
            </div>
          </Modal>
        )
      }
      {
        linkedDetailsModalData && (
          <Modal onClose={() => setLinkedDetailsModalData(null)} title="Linked Field Details">
            {isFetchingLinkedDetails
              ? (
                <Shimmer />
              )
              : (
                <div className={s.scrollableTableContainer}>
                  <Table>
                    <THead>
                      <TableRow>
                        <TableCell head label="Label" className={s.stickyHeaderCell} />
                        <TableCell head label="Section Label" className={s.stickyHeaderCell} />
                        <TableCell head label="Subsection" className={s.stickyHeaderCell} />
                      </TableRow>
                    </THead>
                    <TBody>
                      {linkedDetailsModalData.linkedFieldDetails.map((detail) => (
                        <TableRow key={detail.key}>
                          <TableCell compact label={detail.label} />
                          <TableCell compact label={detail.sectionLabel} />
                          <TableCell compact label={detail.subsection || 'N/A'} />
                        </TableRow>
                      ))}
                    </TBody>
                  </Table>
                </div>
              )}
          </Modal>
        )
      }
    </>
  );
};

export default FormField;
